import React, { useEffect, useMemo, useState } from 'react';
import BrandContainerWithLogo from '../Common/BrandContainerWithLogo';
import { StyledDiv } from '../../Theme/CommonStyled';
import DetailsRow from '../Common/DetailsRow';
import { useNavigate, useParams } from 'react-router';
import Button from '../Common/Button';
import useLandingStore from '../../Store/Landing';
import Title from '../Common/Title';
import contactTitle from '../../Assets/images/TYPO - Contact.svg';
import ContactUsTile from '../Common/ContactUsTile';
import LoadingBox from '../Common/LoadingBox';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import fetchApi from '../../Services/requests';
import useProfileStore from '../../Store/Profile';
import BranchActionSheet from '../Branches/BranchActionSheet';
import {
  ClubLinkBranchInfoType,
  ContactUsViewModelType
} from '../../Types/landing.type';

const Landing = () => {
  const { smsId, customerGuid, smartGuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [uniqueId, setUniqueId] = useState(null);

  const setSmsUrlAction = useLandingStore((state) => state.setSmsUrlAction);
  const getLandingDataAction = useLandingStore(
    (state) => state.getLandingDataAction
  );
  const landingData = useLandingStore((state) => state.landingData);
  const landingLoading = useLandingStore((state) => state.landingLoading);

  const getProfileDataAction = useProfileStore(
    (state) => state.getProfileDataAction
  );

  const [isValidToken, setIsValidToken] = useState(false);
  const [openBranchActionSheet, setOpenBranchActionSheet] = useState(false);

  const { siteUrl, instagram, digitalMenuUrl } = landingData || {};

  const branchData = useMemo(
    () =>
      (landingData &&
        landingData?.clubLinkBranchInfoViewModels?.length === 1 &&
        landingData?.clubLinkBranchInfoViewModels[0]) ||
      ({} as ClubLinkBranchInfoType),
    [landingData]
  );

  const getData = async () => {
    setSmsUrlAction(location.pathname);

    const response = await getLandingDataAction({
      smartGuid,
      smsId,
      customerGuid
    });
    setUniqueId(response?.uniqueId);
  };

  const getProfileData = async () => {
    const response = await getProfileDataAction(landingData?.branchId);
    setIsValidToken(!!response);
  };

  useEffect(() => {
    getData();
    getProfileData();
  }, []);

  useEffect(() => {
    if (uniqueId) {
      if (navigator.geolocation) {
        // Use the getCurrentPosition method to retrieve the user's current location
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await fetchApi('/ClubLink/UpdateGeo', {
              gLat: position.coords.latitude,
              gLong: position.coords.longitude,
              uniqueId: landingData?.uniqueId
            });
          },
          (error) => {
            console.error('Error getting location:', error);
          }
        );
      } else {
        console.error('Geolocation is not supported by this browser.');
      }
    }
  }, [uniqueId]);

  return (
    <LoadingBox loading={landingLoading} skeletonType="brand">
      <StyledDiv pt={'100px'} px={8}>
        <BrandContainerWithLogo
          logo={landingData?.logoUrl}
          brandName={landingData?.brandName}
          logoStyle={{ width: '150px !important', height: '150px !important' }}
          brandNameStyle={{ mt: 11 }}>
          <>
            {landingData?.title && (
              <StyledDiv bg="mainBackground" p="14px" borderRadius="xl" mb={9}>
                <DetailsRow
                  fontSize={4}
                  fontWeight={6}
                  title={t('global.title')}
                  value={
                    <StyledDiv color="primaryGreen">
                      {landingData.title}
                    </StyledDiv>
                  }
                />
              </StyledDiv>
            )}

            {landingData?.message && (
              <StyledDiv
                bg="mainBackground"
                py="18px"
                px="14px"
                maxHeight="300px"
                borderRadius="xl"
                overflow="auto"
                mb={12}>
                {landingData?.message.split('\n').map((item, index) => {
                  return (
                    <StyledDiv
                      fontSize={2}
                      fontWeight={6}
                      mb={
                        landingData?.message.split('\n').length - 1 !== index
                          ? 5
                          : 0
                      }
                      cursor={item.includes('http') && 'pointer'}
                      onClick={(): void => {
                        item.includes('http') && window.open(item, '_blank');
                      }}
                      color={
                        item.includes('http') ? 'primaryBlue' : 'normalText'
                      }
                      key={item}>
                      {item}
                    </StyledDiv>
                  );
                })}
              </StyledDiv>
            )}

            <Button
              onClick={() => {
                navigate(isValidToken ? '/credits' : '/login');
              }}>
              {t('landing.creditsAndGifts')}
            </Button>

            <Title title={<img src={contactTitle} />} mt={12} />

            <StyledDiv
              display="grid"
              gridTemplateColumns={`repeat(4, 1fr )`}
              gridGap="10px"
              mt={11}>
              <ContactUsTile
                isSquare
                href={siteUrl}
                target={'_blank'}
                disabled={!siteUrl}
                icon="language"
                title={t('global.site')}
              />
              <ContactUsTile
                isSquare
                href={`https://instagram.com/${instagram}/`}
                target={'-blank'}
                disabled={!instagram}
                icon="instagram"
                title={t('global.instagram')}
              />
              <ContactUsTile
                isSquare
                icon="menu_1"
                href={digitalMenuUrl}
                disabled={!digitalMenuUrl}
                target={'-blank'}
                title={t('global.menu')}
              />
              {landingData?.clubLinkBranchInfoViewModels.length > 1 ? (
                <Link to={'/branches'}>
                  <ContactUsTile
                    isSquare
                    icon={'phone_2'}
                    title={t('global.contactUs')}
                  />
                </Link>
              ) : (
                <ContactUsTile
                  isSquare
                  icon="phone_2"
                  title={t('global.contactUs')}
                  disabled={Object.keys(branchData)?.length === 0}
                  onClick={() => {
                    setOpenBranchActionSheet(true);
                  }}
                  target={'-blank'}
                />
              )}
            </StyledDiv>
          </>
        </BrandContainerWithLogo>
      </StyledDiv>
      <BranchActionSheet
        branchData={branchData}
        open={openBranchActionSheet}
        onDismiss={setOpenBranchActionSheet}
      />
    </LoadingBox>
  );
};

export default Landing;
